import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { MapPin, Plus, Loader2, AlertTriangle } from "lucide-react";
import { QuickAssignDialog } from "./QuickAssignDialog";
import { AssignmentsTable } from "./AssignmentsTable";
import { fmtH } from "../lib/planning";

export function EmployeeAssignmentsPanel({ employee }) {
    const [rows, setRows] = useState(null);
    const [open, setOpen] = useState(false);
    const cessato = !!employee?.data_cessazione;

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/presenze/assignments", { params: { employee_id: employee.id } });
            setRows(data);
        } catch { toast.error("Errore nel caricamento delle assegnazioni"); setRows([]); }
    }, [employee.id]);

    useEffect(() => { load(); }, [load]);

    const remove = async (r) => {
        if (!window.confirm(`Rimuovere l'assegnazione di ${r.activity_name} presso ${r.worksite_name}?`)) return;
        try {
            await api.delete(`/presenze/assignments/${r.id}`);
            toast.success("Assegnazione rimossa");
            load();
        } catch { toast.error("Errore nella rimozione"); }
    };

    const total = (rows || []).reduce((s, r) => s + (r.hours_week || 0), 0);

    return (
        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm" data-testid="employee-assignments-panel">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]">
                        <MapPin className="h-5 w-5" />
                    </div>
                    <div>
                        <h2 className="font-display text-lg font-bold text-slate-800">Cantieri e attività</h2>
                        <p className="text-xs text-slate-500">
                            Clienti/cantieri assegnati con giorni, orario e frequenza: alimentano la pianificazione settimanale.
                            {rows && rows.length > 0 && (
                                <> Totale <b className="font-mono-numbers text-slate-700" data-testid="employee-assignments-total">{fmtH(total)} h/sett.</b>{employee.contratto_ore ? ` su ${fmtH(employee.contratto_ore)} h contrattuali` : ""}</>
                            )}
                        </p>
                    </div>
                </div>
                {!cessato && (
                    <Button onClick={() => setOpen(true)} className="gap-2 bg-[#376DB3] text-white hover:bg-[#2c5a96]" data-testid="employee-assign-btn">
                        <Plus className="h-4 w-4" /> Assegna cantiere
                    </Button>
                )}
            </div>
            {!employee.timbratura_enabled && rows && rows.length > 0 && (
                <div className="mt-3 flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800" data-testid="employee-assignments-timbratura-note">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    Timbratura non abilitata: le assegnazioni valgono per la pianificazione. Per far timbrare il dipendente abilitalo da Presenze → Abilitazioni.
                </div>
            )}
            <div className="mt-4">
                {!rows ? (
                    <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-slate-300" /></div>
                ) : (
                    <AssignmentsTable rows={rows} onDelete={cessato ? null : remove} showEmployee={false} testPrefix="emp-asg"
                        emptyText="Nessun cantiere assegnato. Usa «Assegna cantiere» per indicare cliente, attività, giorni e orario." />
                )}
            </div>
            <QuickAssignDialog open={open} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} employeeId={employee.id} />
        </div>
    );
}
